import { useState } from 'react';
import { useCountdown } from 'src/hooks';
import { useAppDispatch, useAppSelector } from 'src/redux';

import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { StepPanelHeader } from 'src/components';

import {
  loginReferrer,
  referFriendSelectors,
  resetAuthEmailSentTo,
} from 'src/redux/referFriend';
import { notificationAlert } from 'src/redux/notifications';

import confirmEmailImage from 'src/assets/confirm-email.webp';
import confirmEmailImagePNG from 'src/assets/confirm-email.png';

export const SignInEmailSent: React.FC = () => {
  const dispatch = useAppDispatch();

  const authEmailSentTo = useAppSelector(
    referFriendSelectors.getAuthEmailSentTo,
  );
  const isLoading = useAppSelector(
    referFriendSelectors.getReferrerIsPerformingAction,
  );

  const [timerEndDate, setTimerEndDate] = useState<Date | null>(null);

  const countdown = useCountdown(timerEndDate, () => setTimerEndDate(null));

  const handleChangeEmail = () => {
    dispatch(resetAuthEmailSentTo());
  };

  const handleResendEmail = async () => {
    await dispatch(loginReferrer({ email: authEmailSentTo! }));
    setTimerEndDate(new Date(new Date().getTime() + 31000));

    dispatch(
      notificationAlert(
        `New email with the link has been sent. Please check inbox and spam just in case.`,
      ),
    );
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="column"
        sx={{ width: '100%' }}
      >
        <Box
          sx={(theme) => ({
            background: theme.palette.common.white,
            padding: '62px 100px',
            borderRadius: '48px',
            maxWidth: '574px',
            width: '100%',
            [theme.breakpoints.down('md')]: {
              padding: '48px 32px',
            },
          })}
        >
          <Stack gap={3} sx={{ textAlign: 'center' }}>
            <StepPanelHeader
              srcSetPng={confirmEmailImagePNG}
              srcSetWebp={confirmEmailImage}
              alt="Confirm email"
              imageWidth={{ xs: 57, sm: 90, xxl: 100 }}
            >
              Please check your inbox
            </StepPanelHeader>

            <Typography variant="body1" textAlign="center">
              There is a confirmation email with a magic link to continue your
              process
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
            >
              {authEmailSentTo}
            </Typography>

            <Stack
              sx={{
                padding: '24px 32px 28px 32px',
                borderRadius: '4px',
                gap: '4px',
                background: '#F6F9FD',
              }}
            >
              <Stack direction="row" gap={1}>
                <Typography variant="body2" color="text.secondary">
                  Incorrect email?
                </Typography>
                <Typography
                  onClick={handleChangeEmail}
                  variant="body2"
                  color="brand.accent"
                  fontWeight="bold"
                  sx={{
                    opacity: '0.8',
                    ':hover': {
                      opacity: '1',
                      cursor: 'pointer',
                    },
                  }}
                  data-testid="edit-email-button"
                >
                  Change email →
                </Typography>
              </Stack>

              <Stack direction="row" gap={1}>
                <Typography variant="body2" color="text.secondary">
                  Not receiving?
                </Typography>
                {isLoading && (
                  <CircularProgress
                    size={16}
                    sx={{ color: (theme) => theme.palette.brand.accent }}
                  />
                )}
                {Boolean(timerEndDate) && !isLoading && (
                  <Typography variant="body2" color="text.secondary">
                    <b>
                      {countdown.minutes} :{' '}
                      {countdown.seconds < 10
                        ? `0${countdown.seconds}`
                        : countdown.seconds}
                      {'s until retry'}
                    </b>
                  </Typography>
                )}
                {!Boolean(timerEndDate) && !isLoading && (
                  <Typography
                    onClick={handleResendEmail}
                    variant="body2"
                    color="brand.accent"
                    fontWeight="bold"
                    sx={{
                      opacity: '0.8',
                      ':hover': {
                        opacity: '1',
                        cursor: 'pointer',
                      },
                    }}
                    data-testid="resend-email-button"
                  >
                    Resend email →
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </>
  );
};
